import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, CheckCircle, Plus, Minus, DollarSign, TrendingUp, Receipt, Percent, Coins } from 'lucide-react';
import { formatCurrencyText } from '../../utils/chartFormatters';
import { createSnapshotFromCurrentData, validateSnapshot } from '../../utils/wealthTracking/snapshotCalculations';
import { createSnapshot, getMostRecentSnapshot, updateSnapshot } from '../../utils/wealthTracking/snapshotManager';
import { useCountry } from '../../CountryContext';
import { getCurrencySymbol, getCurrencyCode } from '../../countryConfig';
import { useAuth } from '../../AuthContext';
import InfoTooltip from './InfoTooltip';
import { calculateMonthlyContribution } from '../../utils/accountUtils';

const CreateSnapshotModal = ({
  isOpen,
  onClose,
  onSnapshotCreated,
  editingSnapshot = null, // NEW: If provided, we're editing instead of creating
  accounts = [],
  investments = [],
  cryptoHoldings = [],
  liabilities = [],
  cashReserves = 0,
  exchangeRates = null, // ⭐ NEW: Exchange rates for currency conversion
  isDark
}) => {
  const { currentUser } = useAuth();
  const { selectedCountry } = useCountry();
  const currencySymbol = getCurrencySymbol(selectedCountry);

  const isEditMode = !!editingSnapshot;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Accounts, 2: Liabilities, 3: Review
  const [notes, setNotes] = useState('');

  // Account inputs: { accountId: { contributions, withdrawals, dividendsInterest, fees } }
  const [accountInputs, setAccountInputs] = useState({});

  // Liability inputs: { liabilityId: { principalPaid, interestPaid } }
  const [liabilityInputs, setLiabilityInputs] = useState({});

  // Initialize inputs for all accounts (with pre-population for edit mode OR auto-calculated from account settings)
  useEffect(() => {
    if (isOpen && accounts.length > 0) {
      const initialAccountInputs = {};

      accounts.forEach(account => {
        // If editing, try to find existing data for this account
        const existingAccountData = isEditMode && editingSnapshot?.accounts
          ? editingSnapshot.accounts.find(a => a.accountId === account.id)
          : null;

        // ✓ UNIFIED CONTRIBUTION TRACKING: Auto-populate contributions from account settings
        // Users can still override these values if needed
        const defaultContribution = existingAccountData?.contributions
          || calculateMonthlyContribution(account)
          || 0;

        initialAccountInputs[account.id] = {
          contributions: defaultContribution,
          withdrawals: existingAccountData?.withdrawals || 0,
          dividendsInterest: existingAccountData?.dividendsInterest || 0,
          fees: existingAccountData?.fees || 0,
          investmentsSold: existingAccountData?.investmentsSold || 0,
          tax: existingAccountData?.tax || 0
        };
      });

      setAccountInputs(initialAccountInputs);
    }
  }, [isOpen, accounts, isEditMode, editingSnapshot]);

  // Initialize inputs for all liabilities (with pre-population for edit mode)
  useEffect(() => {
    if (isOpen && liabilities.length > 0) {
      const initialLiabilityInputs = {};

      liabilities.forEach(liability => {
        // If editing, try to find existing data for this liability
        const existingLiabilityData = isEditMode && editingSnapshot?.liabilities
          ? editingSnapshot.liabilities.find(l => l.liabilityId === liability.id)
          : null;

        initialLiabilityInputs[liability.id] = {
          principalPaid: existingLiabilityData?.principalPaid || 0,
          interestPaid: existingLiabilityData?.interestPaid || 0
        };
      });

      setLiabilityInputs(initialLiabilityInputs);
    }
  }, [isOpen, liabilities, isEditMode, editingSnapshot]);

  // Pre-populate notes when editing
  useEffect(() => {
    if (isOpen && isEditMode && editingSnapshot) {
      setNotes(editingSnapshot.notes || '');
    } else if (isOpen && !isEditMode) {
      setNotes('');
    }
  }, [isOpen, isEditMode, editingSnapshot]);

  const handleAccountInputChange = (accountId, field, value) => {
    setAccountInputs(prev => ({
      ...prev,
      [accountId]: {
        ...prev[accountId],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const handleLiabilityInputChange = (liabilityId, field, value) => {
    setLiabilityInputs(prev => ({
      ...prev,
      [liabilityId]: {
        ...prev[liabilityId],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const handleSaveSnapshot = async () => {
    setLoading(true);
    setError(null);

    try {
      if (isEditMode) {
        // EDIT MODE: Update existing snapshot with new contribution/withdrawal data
        // Recalculate market growth based on new inputs
        const previousSnapshot = await getMostRecentSnapshot(currentUser.uid);

        // Create fresh snapshot calculation with updated user inputs
        // ⭐ MULTI-CURRENCY: Pass exchange rates for conversion
        const displayCurrency = getCurrencyCode(selectedCountry);
        const recalculatedSnapshot = createSnapshotFromCurrentData({
          investments,
          cryptoHoldings,
          liabilities,
          accounts,
          cashReserves,
          userInputs: {
            accounts: accountInputs,
            liabilities: liabilityInputs
          },
          previousSnapshot,
          snapshotType: editingSnapshot.snapshotType || 'manual',
          notes,
          displayCurrency,
          exchangeRates
        });

        // Validate
        const validation = validateSnapshot(recalculatedSnapshot);
        if (!validation.valid) {
          setError(validation.errors.join(', '));
          setLoading(false);
          return;
        }

        // Update in Firestore
        await updateSnapshot(currentUser.uid, editingSnapshot.id, recalculatedSnapshot);

        // Notify parent
        onSnapshotCreated({ id: editingSnapshot.id, ...recalculatedSnapshot });

      } else {
        // CREATE MODE: Create new snapshot
        const previousSnapshot = await getMostRecentSnapshot(currentUser.uid);

        // Create snapshot from current data
        // ⭐ MULTI-CURRENCY: Pass exchange rates for conversion
        const displayCurrency = getCurrencyCode(selectedCountry);
        const snapshot = createSnapshotFromCurrentData({
          investments,
          cryptoHoldings,
          liabilities,
          accounts,
          cashReserves,
          userInputs: {
            accounts: accountInputs,
            liabilities: liabilityInputs
          },
          previousSnapshot,
          snapshotType: 'manual',
          notes,
          displayCurrency,
          exchangeRates
        });

        // Validate
        const validation = validateSnapshot(snapshot);
        if (!validation.valid) {
          setError(validation.errors.join(', '));
          setLoading(false);
          return;
        }

        // Save to Firestore
        const savedSnapshot = await createSnapshot(currentUser.uid, snapshot);

        // Notify parent
        onSnapshotCreated(savedSnapshot);
      }

      // Reset and close
      resetForm();
      onClose();
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} snapshot:`, err);
      setError(err.message || `Failed to ${isEditMode ? 'update' : 'create'} snapshot`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setNotes('');
    setError(null);
    setAccountInputs({});
    setLiabilityInputs({});
  };

  const getTotalContributions = () => {
    return Object.values(accountInputs).reduce((sum, inputs) => sum + (inputs.contributions || 0), 0);
  };

  const getTotalWithdrawals = () => {
    return Object.values(accountInputs).reduce((sum, inputs) => sum + (inputs.withdrawals || 0), 0);
  };

  const getTotalDividends = () => {
    return Object.values(accountInputs).reduce((sum, inputs) => sum + (inputs.dividendsInterest || 0), 0);
  };

  const getTotalInvestmentsSold = () => {
    return Object.values(accountInputs).reduce((sum, inputs) => sum + (inputs.investmentsSold || 0), 0);
  };

  const getTotalTax = () => {
    return Object.values(accountInputs).reduce((sum, inputs) => sum + (inputs.tax || 0), 0);
  };

  const getTotalCharges = () => {
    return Object.values(accountInputs).reduce((sum, inputs) => sum + (inputs.fees || 0), 0);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-50">
      <div
        className={`w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden rounded-lg shadow-xl ${
          isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
        }`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-2xl font-bold truncate">
              {isEditMode ? 'Edit Wealth Snapshot' : 'Create Wealth Snapshot'}
            </h2>
            <p className={`text-xs sm:text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {isEditMode && editingSnapshot?.month && (
                <span className="font-semibold text-blue-500 mr-2">
                  {editingSnapshot.month}
                </span>
              )}
              Step {step} of 3: {step === 1 ? 'Account Activity' : step === 2 ? 'Liability Payments' : 'Review & Save'}
            </p>
          </div>
          <button
            onClick={() => { resetForm(); onClose(); }}
            className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(95vh-200px)] sm:max-h-[60vh]">
          {error && (
            <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500 flex items-center gap-2 text-red-500">
              <AlertCircle className="w-5 h-5" />
              <p>{error}</p>
            </div>
          )}

          {/* Step 1: Account Activity */}
          {step === 1 && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'} border border-blue-500/30`}>
                <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                  <strong>✓ Contributions are auto-populated</strong> from your account settings.
                  Enter any activity for this month: withdrawals, dividends received, realized gains from sales, tax withheld, and platform charges.
                  Leave fields as 0 if there was no activity.
                </p>
              </div>

              {accounts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No accounts found. Add accounts first to create a snapshot.</p>
                </div>
              ) : (
                accounts.map(account => (
                  <div
                    key={account.id}
                    className={`p-3 sm:p-4 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
                  >
                    <div className="mb-3">
                      <h3 className="font-semibold text-sm sm:text-base">{account.name}</h3>
                      <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {account.type}{account.institution ? ` • ${account.institution}` : ''}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {/* Contributions */}
                      <div>
                        <label className={`text-sm flex items-center gap-2 mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          <Plus className="w-4 h-4 text-green-500" />
                          Contributions
                          <InfoTooltip
                            content="Money you added to this account this month (deposits, transfers in, employer contributions, etc.)"
                            isDark={isDark}
                            position="top"
                          />
                        </label>
                        <div className="flex items-center">
                          <span className={`px-3 py-2 border-y border-l rounded-l-lg ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}>
                            {currencySymbol}
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={accountInputs[account.id]?.contributions || 0}
                            onChange={(e) => handleAccountInputChange(account.id, 'contributions', e.target.value)}
                            className={`flex-1 px-3 py-2 border-y border-r rounded-r-lg ${
                              isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        </div>
                      </div>

                      {/* Withdrawals */}
                      <div>
                        <label className={`text-sm flex items-center gap-2 mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          <Minus className="w-4 h-4 text-red-500" />
                          Withdrawals
                          <InfoTooltip
                            content="Money you took out of this account this month (withdrawals, transfers out, spending, etc.)"
                            isDark={isDark}
                            position="top"
                          />
                        </label>
                        <div className="flex items-center">
                          <span className={`px-3 py-2 border-y border-l rounded-l-lg ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}>
                            {currencySymbol}
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={accountInputs[account.id]?.withdrawals || 0}
                            onChange={(e) => handleAccountInputChange(account.id, 'withdrawals', e.target.value)}
                            className={`flex-1 px-3 py-2 border-y border-r rounded-r-lg ${
                              isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        </div>
                      </div>

                      {/* Dividends */}
                      <div>
                        <label className={`text-sm flex items-center gap-2 mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          <Coins className="w-4 h-4 text-blue-500" />
                          Dividends
                          <InfoTooltip
                            content="Dividend income received this month from stocks, funds, or other investments in this account."
                            isDark={isDark}
                            position="top"
                          />
                        </label>
                        <div className="flex items-center">
                          <span className={`px-3 py-2 border-y border-l rounded-l-lg ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}>
                            {currencySymbol}
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={accountInputs[account.id]?.dividendsInterest || 0}
                            onChange={(e) => handleAccountInputChange(account.id, 'dividendsInterest', e.target.value)}
                            className={`flex-1 px-3 py-2 border-y border-r rounded-r-lg ${
                              isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        </div>
                      </div>

                      {/* Investments Sold (Realized Gains) */}
                      <div>
                        <label className={`text-sm flex items-center gap-2 mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          <TrendingUp className="w-4 h-4 text-purple-500" />
                          Investments Sold
                          <InfoTooltip
                            content="Realized profit/loss from selling investments this month. Enter profit as positive, loss as negative."
                            isDark={isDark}
                            position="top"
                          />
                        </label>
                        <div className="flex items-center">
                          <span className={`px-3 py-2 border-y border-l rounded-l-lg ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}>
                            {currencySymbol}
                          </span>
                          <input
                            type="number"
                            step="0.01"
                            value={accountInputs[account.id]?.investmentsSold || 0}
                            onChange={(e) => handleAccountInputChange(account.id, 'investmentsSold', e.target.value)}
                            className={`flex-1 px-3 py-2 border-y border-r rounded-r-lg ${
                              isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        </div>
                      </div>

                      {/* Tax */}
                      <div>
                        <label className={`text-sm flex items-center gap-2 mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          <Percent className="w-4 h-4 text-orange-500" />
                          Tax
                          <InfoTooltip
                            content="Tax withheld or paid on investment income/gains this month (e.g., dividend withholding tax, capital gains tax)."
                            isDark={isDark}
                            position="top"
                          />
                        </label>
                        <div className="flex items-center">
                          <span className={`px-3 py-2 border-y border-l rounded-l-lg ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}>
                            {currencySymbol}
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={accountInputs[account.id]?.tax || 0}
                            onChange={(e) => handleAccountInputChange(account.id, 'tax', e.target.value)}
                            className={`flex-1 px-3 py-2 border-y border-r rounded-r-lg ${
                              isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        </div>
                      </div>

                      {/* Fees/Charges */}
                      <div>
                        <label className={`text-sm flex items-center gap-2 mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          <Receipt className="w-4 h-4 text-red-400" />
                          Charges
                          <InfoTooltip
                            content="Platform fees, trading fees, management fees, or other charges deducted this month."
                            isDark={isDark}
                            position="top"
                          />
                        </label>
                        <div className="flex items-center">
                          <span className={`px-3 py-2 border-y border-l rounded-l-lg ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}>
                            {currencySymbol}
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={accountInputs[account.id]?.fees || 0}
                            onChange={(e) => handleAccountInputChange(account.id, 'fees', e.target.value)}
                            className={`flex-1 px-3 py-2 border-y border-r rounded-r-lg ${
                              isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Step 2: Liability Payments */}
          {step === 2 && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'} border border-blue-500/30`}>
                <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                  Leave as 0 to auto-calculate using amortization. Override only if you have exact statement values.
                </p>
              </div>

              {liabilities.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No liabilities found. Skip to review.</p>
                </div>
              ) : (
                liabilities.map(liability => (
                  <div
                    key={liability.id}
                    className={`p-4 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
                  >
                    <h3 className="font-semibold mb-1">{liability.name}</h3>
                    <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Current balance: {formatCurrencyText(liability.currentBalance, currencySymbol)}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Principal Paid */}
                      <div>
                        <label className={`text-sm mb-1 flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Principal Paid
                          <InfoTooltip
                            content="The portion of your payment that reduces the loan balance. Leave at 0 to auto-calculate using amortization formula based on your interest rate."
                            isDark={isDark}
                            position="top"
                          />
                        </label>
                        <div className="flex items-center">
                          <span className={`px-3 py-2 border-y border-l rounded-l-lg ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}>
                            {currencySymbol}
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={liabilityInputs[liability.id]?.principalPaid || 0}
                            onChange={(e) => handleLiabilityInputChange(liability.id, 'principalPaid', e.target.value)}
                            className={`flex-1 px-3 py-2 border-y border-r rounded-r-lg ${
                              isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        </div>
                      </div>

                      {/* Interest Paid */}
                      <div>
                        <label className={`text-sm mb-1 flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Interest Paid
                          <InfoTooltip
                            content="The interest portion of your payment. This is the cost of borrowing and does not reduce the loan balance. Leave at 0 to auto-calculate."
                            isDark={isDark}
                            position="top"
                          />
                        </label>
                        <div className="flex items-center">
                          <span className={`px-3 py-2 border-y border-l rounded-l-lg ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}>
                            {currencySymbol}
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={liabilityInputs[liability.id]?.interestPaid || 0}
                            onChange={(e) => handleLiabilityInputChange(liability.id, 'interestPaid', e.target.value)}
                            className={`flex-1 px-3 py-2 border-y border-r rounded-r-lg ${
                              isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="space-y-4">
              <div className={`p-6 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <h3 className="font-semibold mb-4">Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Contributed and Withdrawn */}
                  <div className="space-y-2">
                    <h4 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Contributed and Withdrawn
                    </h4>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Cash paid in:</span>
                      <span className="font-semibold text-green-500">
                        +{formatCurrencyText(getTotalContributions(), currencySymbol)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Cash withdrawn:</span>
                      <span className="font-semibold text-red-500">
                        -{formatCurrencyText(getTotalWithdrawals(), currencySymbol)}
                      </span>
                    </div>
                    <div className={`flex justify-between pt-2 border-t ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Net:</span>
                      <span className={`font-semibold ${getTotalContributions() - getTotalWithdrawals() >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {formatCurrencyText(getTotalContributions() - getTotalWithdrawals(), currencySymbol)}
                      </span>
                    </div>
                  </div>

                  {/* Investment Returns */}
                  <div className="space-y-2">
                    <h4 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Investment Returns
                    </h4>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Dividends:</span>
                      <span className="font-semibold text-blue-500">
                        +{formatCurrencyText(getTotalDividends(), currencySymbol)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Investments sold:</span>
                      <span className={`font-semibold ${getTotalInvestmentsSold() >= 0 ? 'text-purple-500' : 'text-red-500'}`}>
                        {formatCurrencyText(getTotalInvestmentsSold(), currencySymbol)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Tax:</span>
                      <span className="font-semibold text-orange-500">
                        -{formatCurrencyText(getTotalTax(), currencySymbol)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Charges:</span>
                      <span className="font-semibold text-red-400">
                        -{formatCurrencyText(getTotalCharges(), currencySymbol)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Accounts and Liabilities count */}
                <div className={`mt-4 pt-4 border-t ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
                  <div className="flex justify-between">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Accounts Tracked:</span>
                    <span className="font-semibold">{accounts.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Liabilities Tracked:</span>
                    <span className="font-semibold">{liabilities.length}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className={`text-sm mb-1 block ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about this month..."
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-0 p-4 sm:p-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="order-2 sm:order-1">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className={`w-full sm:w-auto px-4 py-2 rounded-lg text-sm sm:text-base ${
                  isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                Back
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 order-1 sm:order-2">
            <button
              onClick={() => { resetForm(); onClose(); }}
              className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm sm:text-base ${
                isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              Cancel
            </button>

            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm sm:text-base font-semibold"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSaveSnapshot}
                disabled={loading}
                className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white flex items-center justify-center gap-2 disabled:opacity-50 text-sm sm:text-base font-semibold"
              >
                {loading ? (
                  <>{isEditMode ? 'Updating...' : 'Creating...'}</>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>{isEditMode ? 'Update Snapshot' : 'Create Snapshot'}</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateSnapshotModal;
